import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {MuiColorInput} from 'mui-color-input';
import {AssetAutoList} from '@netx/core-asset-next/components/AssetAutoList';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {SearchSubTypeLabel, SearchRuleWarningLabel} from '../../../../locales';
import {CommonRuleEditorProps} from '../../../types';
import {VisualType} from '../../constants';
import {VisualTypeLabel} from '../../locales';
import {
	visualRuleEditorClasses,
	getVisualRuleEditorUtilityClass,
	VisualRuleEditorClasses,
	VISUAL_RULE_EDITOR_CLASS_NAME,
} from './visualRuleEditorClasses';

export type VisualRuleEditorOwnerState = WithClasses<
	VisualRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: VisualRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getVisualRuleEditorUtilityClass, classes);
};

const VisualRuleEditorRoot = styled('div', {
	name: VISUAL_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	'& > * + *': {
		marginLeft: `${theme.spacing(1)} !important`,
	},
}));

const getSubType1OptionLabel = (option: VisualType) => VisualTypeLabel[option];

export type VisualRuleEditorProps = WithClasses<
	VisualRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: VisualType;
		elementSubType1Options: VisualType[];
		elementValue1: number | string;
		elementValue1Unavailable: boolean;
		elementValue1Label: string;
		elementValue1Loading: boolean;
		onSubType1Change: (subType1: VisualType) => void;
		onValue1Change: (value: number | string) => void;
	}
>;

const VisualRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	elementValue1Label,
	elementValue1Loading,
	elementValue1Unavailable,
	validationErrors,
	onSubType1Change,
	onValue1Change,
}: VisualRuleEditorProps) => {
	const [assetName, setAssetName] = useState('');

	const ownerState: VisualRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	useEffect(() => {
		if (elementSubType1 === VisualType.COLOR) {
			setAssetName('');
			return;
		}
		setAssetName(elementValue1Label);
	}, [elementValue1Label, elementSubType1]);

	const waitingForAsset = elementValue1Loading && assetName === '';

	return (
		<VisualRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<VisualType, false, true, false>
				data-testid="visual-rule-sub-type-1"
				disableClearable
				label={SearchSubTypeLabel.MATCH_TYPE}
				getOptionLabel={getSubType1OptionLabel}
				name={'rule-sub-type-1'}
				options={elementSubType1Options}
				value={elementSubType1}
				onChange={(event, value) => onSubType1Change(value)}
			/>

			{elementSubType1 === VisualType.COLOR ? (
				<MuiColorInput
					data-testid="visual-rule-value-1"
					error={!!validationErrors?.elementValue1}
					helperText={validationErrors?.elementValue1?.message as any}
					label={VisualTypeLabel[2]}
					format="hex"
					isAlphaHidden
					fallbackValue="#000000"
					name="rule-value-1"
					value={elementValue1.toString()}
					onChange={onValue1Change}
				/>
			) : (
				<AssetAutoList<false, true>
					data-testid="visual-rule-value-1"
					disableClearable
					error={validationErrors?.elementValue1?.message}
					loadingValue={elementValue1Loading}
					name="rule-value-1"
					placeholder={
						waitingForAsset
							? 'loading...'
							: elementValue1Unavailable
							? SearchRuleWarningLabel.DATA_UNAVAILABLE
							: undefined
					}
					value={
						elementValue1Unavailable || waitingForAsset
							? (null as any)
							: {
									label: assetName,
									value: elementValue1,
							  }
					}
					onChange={(event, asset) => {
						setAssetName(asset.label);
						onValue1Change(asset.value);
					}}
					InputProps={{
						InputLabelProps: {
							shrink: elementValue1Unavailable || undefined,
						},
					}}
				/>
			)}
		</VisualRuleEditorRoot>
	);
};

export const VisualRuleEditorPrimitive = VisualRuleEditorInner;

export const VisualRuleEditor = VisualRuleEditorPrimitive;

export default VisualRuleEditor;

export type VisualRuleEditorGenericProps = WithClasses<
	VisualRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const VisualRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: VisualRuleEditorGenericProps) => {
	return (
		<VisualRuleEditor
			{...props}
			elementValue1={
				Array.isArray(elementValue1) ? elementValue1[0] : elementValue1
			}
		/>
	);
};

export const VisualRuleEditorGeneric = VisualRuleEditorGenericPrimitive;
